import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  USER_INFO,
} from "@/services/client/types";
import { localStorageService } from "@/utils/localStorage";
import { jwtDecode } from "jwt-decode";
import { create } from "zustand";

export interface User {
  country_code: string;
  email: string;
  employee_id: string;
  exp: number;
  first_name: string;
  iat: number;
  id: number;
  last_name: string;
}
interface AuthState {
  isLoggedIn: boolean;
  user: User | null;
  initializeAuth: () => void;
  setUserInfo: (user: User) => void;
  _handleVerifySuccess: (token: string, refresh: string) => void;
  _handleLogoutSuccess: () => void;
}

const getInitialIsLoggedIn = (): boolean => {
  try {
    const token = localStorageService.getItem(ACCESS_TOKEN);
    return !!token;
  } catch (error) {
    console.error("Error reading auth token from localStorage:", error);
    return false;
  }
};

const setUserInfo = (user: User) => {
  localStorageService.setItem(USER_INFO, JSON.stringify(user));
};

const getUserInfo = () => {
  try {
    const token = localStorageService.getItem(ACCESS_TOKEN);
    if (!token) return null;
    const user: User = jwtDecode(token);
    return user;
  } catch (error) {
    console.error("Error reading auth token from localStorage:", error);
    return null;
  }
};

const useAuthStore = create<AuthState>((set) => ({
  isLoggedIn: getInitialIsLoggedIn(),
  user: getUserInfo(),

  initializeAuth: () => {
    set({
      isLoggedIn: getInitialIsLoggedIn(),
      user: getUserInfo(),
    });
  },

  setUserInfo: (user: User) => {
    setUserInfo(user);
    set({
      user: user,
    });
  },

  _handleVerifySuccess: (token: string, refresh: string) => {
    localStorageService.setItem(ACCESS_TOKEN, token);
    localStorageService.setItem(REFRESH_TOKEN, refresh);
    set({
      isLoggedIn: true,
    });
  },

  _handleLogoutSuccess: () => {
    localStorageService.removeItem(ACCESS_TOKEN);
    localStorageService.removeItem(REFRESH_TOKEN);
    localStorageService.removeItem(USER_INFO);
  },
}));

export default useAuthStore;
